/**
 * PublicSitePage — `/s/<slug>` (no auth, public, SEO-indexable)
 *
 * Universal-modern aesthetic — clean lines, generous spacing, soft depth,
 * Framer Motion scroll reveals. Two render modes:
 *   - slug mode  (default)     — `/s/<slug>` route, fetches by slug
 *   - host mode  (custom dom)  — App.js renders <PublicSitePage hostMode />
 *     when window.location.hostname is a custom domain. Fetches by Host.
 *
 * Sections (any combination, in this order):
 *   Nav · Hero · Trust · Stats · About · Services · Testimonials · FAQ ·
 *   Hours/Contact · Contact form · Footer
 */
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Mail, Phone, MapPin, Clock, ArrowRight, Send, Twitter, Linkedin,
  Instagram, Facebook, Globe as GlobeIcon, CheckCircle2, Loader2,
  Menu, X, Star, ChevronDown, Calculator, TrendingUp, ShieldCheck,
  FileText, Briefcase, Users, BarChart, DollarSign, Lightbulb, Target,
  Rocket, Heart, Award, Headphones,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Theme tokens (typography + surfaces) ───────────────────────────────────
const themes = {
  professional: {
    bodyFont: "'Inter', system-ui, sans-serif",
    headingFont: "'Inter', system-ui, sans-serif",
    headlineWeight: 'font-bold',
    headlineTracking: 'tracking-tight',
    heroBg: 'bg-white',
    pageBg: 'bg-white',
    altBg: 'bg-zinc-50',
    cardBg: 'bg-white',
    cardBorder: 'border-zinc-200',
    textPrimary: 'text-zinc-900',
    textMuted: 'text-zinc-600',
    textSubtle: 'text-zinc-500',
    navBg: 'bg-white/80',
    navBorder: 'border-zinc-200/80',
  },
  warm: {
    bodyFont: "'Inter', system-ui, sans-serif",
    headingFont: "'Inter', system-ui, sans-serif",
    headlineWeight: 'font-bold',
    headlineTracking: 'tracking-tight',
    heroBg: 'bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50',
    pageBg: 'bg-amber-50/30',
    altBg: 'bg-white',
    cardBg: 'bg-white',
    cardBorder: 'border-amber-200/60',
    textPrimary: 'text-zinc-900',
    textMuted: 'text-zinc-600',
    textSubtle: 'text-zinc-500',
    navBg: 'bg-amber-50/80',
    navBorder: 'border-amber-200/60',
  },
  bold: {
    bodyFont: "'Inter', system-ui, sans-serif",
    headingFont: "'Inter', system-ui, sans-serif",
    headlineWeight: 'font-extrabold',
    headlineTracking: 'tracking-tighter',
    heroBg: 'bg-zinc-950 text-white',
    pageBg: 'bg-zinc-950 text-white',
    altBg: 'bg-zinc-900 text-white',
    cardBg: 'bg-zinc-900',
    cardBorder: 'border-zinc-800',
    textPrimary: 'text-white',
    textMuted: 'text-zinc-400',
    textSubtle: 'text-zinc-500',
    navBg: 'bg-zinc-950/80',
    navBorder: 'border-zinc-800',
  },
};

const SOCIAL_ICONS = {
  twitter: Twitter, linkedin: Linkedin, instagram: Instagram,
  facebook: Facebook, website: GlobeIcon,
};

// Service icon map — keys match the AI prompt's allowed list
const SERVICE_ICONS = {
  'calculator': Calculator, 'trending-up': TrendingUp, 'shield-check': ShieldCheck,
  'file-text': FileText, 'briefcase': Briefcase, 'users': Users,
  'bar-chart': BarChart, 'clock': Clock, 'dollar-sign': DollarSign,
  'globe': GlobeIcon, 'lightbulb': Lightbulb, 'target': Target,
  'rocket': Rocket, 'heart': Heart, 'award': Award, 'headphones': Headphones,
};

const REVEAL = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
};

// Convert hex → rgba for tinted overlays
function hexToRgba(hex, alpha = 0.1) {
  const m = (hex || '').replace('#', '').match(/^([0-9a-f]{6})$/i);
  if (!m) return `rgba(16, 185, 129, ${alpha})`;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default function PublicSitePage({ hostMode = false }) {
  const { slug: slugFromUrl } = useParams();
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = hostMode
          ? await fetch(`${API_URL}/api/public/sites/by-host?host=${encodeURIComponent(window.location.hostname)}`)
          : await fetch(`${API_URL}/api/public/sites/${slugFromUrl}`);
        if (r.status === 404) { setNotFound(true); return; }
        if (!r.ok) throw new Error();
        setSite(await r.json());
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slugFromUrl, hostMode]);

  // SEO metadata
  useEffect(() => {
    if (!site) return;
    const title = site.seo?.meta_title || site.workspace_name;
    const desc = site.seo?.meta_description || site.sections?.hero?.subheadline || '';
    document.title = title;
    let m = document.querySelector('meta[name="description"]');
    if (!m) {
      m = document.createElement('meta');
      m.setAttribute('name', 'description');
      document.head.appendChild(m);
    }
    m.setAttribute('content', desc);
    return () => { document.title = 'CountBucks'; };
  }, [site]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }
  if (notFound || !site) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4">
        <div className="max-w-md text-center">
          <h1 className="text-3xl font-bold mb-3">Site not found</h1>
          <p className="text-zinc-600 mb-6">We couldn't find a published site at this address.</p>
          <Link to="/" className="text-emerald-600 hover:underline">Go home</Link>
        </div>
      </div>
    );
  }

  const t = themes[site.theme] || themes.professional;
  const brand = site.brand_color || '#10b981';
  const s = site.sections || {};
  const isDark = site.theme === 'bold';
  const slug = site.slug || slugFromUrl;

  // Auto-derive nav links from visible sections (cheap; recompute on render)
  const navItems = (() => {
    const items = [{ id: 'top', label: 'Home' }];
    if (s.about?.visible) items.push({ id: 'about', label: 'About' });
    if (s.services?.visible && (s.services.items || []).length > 0) items.push({ id: 'services', label: 'Services' });
    if (s.testimonials?.visible && (s.testimonials.items || []).length > 0) items.push({ id: 'testimonials', label: 'Testimonials' });
    if (s.faq?.visible && (s.faq.items || []).length > 0) items.push({ id: 'faq', label: 'FAQ' });
    items.push({ id: 'contact', label: 'Contact' });
    return items;
  })();

  return (
    <div
      id="top"
      className={`min-h-screen ${t.pageBg} ${t.textPrimary}`}
      style={{ fontFamily: t.bodyFont, '--brand': brand }}
      data-testid="public-site-page"
    >
      <NavBar
        site={site}
        navItems={navItems}
        brand={brand}
        t={t}
        isDark={isDark}
      />

      {/* HERO */}
      {s.hero?.visible && <Hero s={s.hero} site={site} t={t} brand={brand} isDark={isDark} />}

      {/* TRUST BAR — sits right under hero for max prominence */}
      {s.trust?.visible && (s.trust.items || []).length > 0 && (
        <TrustBar s={s.trust} t={t} brand={brand} isDark={isDark} />
      )}

      {/* STATS */}
      {s.stats?.visible && (s.stats.items || []).length > 0 && (
        <Stats s={s.stats} t={t} brand={brand} isDark={isDark} />
      )}

      {/* ABOUT */}
      {s.about?.visible && s.about?.body && (
        <section id="about" className={`${t.altBg} py-20 sm:py-28`}>
          <motion.div {...REVEAL} className="max-w-3xl mx-auto px-6">
            <SectionLabel brand={brand}>About</SectionLabel>
            <h2 className={`text-3xl sm:text-4xl ${t.headlineWeight} ${t.headlineTracking} mt-3 mb-8`} style={{ fontFamily: t.headingFont }}>
              {site.workspace_name ? `Meet ${site.workspace_name}.` : 'About us.'}
            </h2>
            <p className={`text-lg leading-relaxed ${t.textMuted} whitespace-pre-line`}>
              {s.about.body}
            </p>
          </motion.div>
        </section>
      )}

      {/* SERVICES */}
      {s.services?.visible && (s.services.items || []).length > 0 && (
        <Services s={s.services} t={t} brand={brand} isDark={isDark} />
      )}

      {/* TESTIMONIALS */}
      {s.testimonials?.visible && (s.testimonials.items || []).length > 0 && (
        <Testimonials s={s.testimonials} t={t} brand={brand} isDark={isDark} />
      )}

      {/* FAQ */}
      {s.faq?.visible && (s.faq.items || []).length > 0 && (
        <FAQ s={s.faq} t={t} brand={brand} isDark={isDark} />
      )}

      {/* HOURS / CONTACT INFO */}
      {s.hours_contact?.visible && <HoursContact s={s.hours_contact} t={t} brand={brand} isDark={isDark} />}

      {/* CONTACT FORM */}
      {s.contact_form?.visible && (
        <ContactFormSection
          slug={slug}
          theme={t}
          brand={brand}
          isDark={isDark}
          title={s.contact_form.title}
          intro={s.contact_form.intro}
          services={s.services?.items || []}
        />
      )}

      {/* FOOTER */}
      <Footer site={site} s={s} brand={brand} isDark={isDark} t={t} />
    </div>
  );
}

// ── Reusable: section eyebrow label ────────────────────────────────────────
function SectionLabel({ children, brand }) {
  return (
    <p
      className="text-xs font-bold uppercase tracking-[0.18em]"
      style={{ color: brand }}
    >
      {children}
    </p>
  );
}

// ── Nav bar ─────────────────────────────────────────────────────────────────
function NavBar({ site, navItems, brand, t, isDark }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <nav
      className={`sticky top-0 z-50 backdrop-blur-md transition-all duration-300 ${
        scrolled ? `${t.navBg} border-b ${t.navBorder} shadow-sm` : 'bg-transparent'
      }`}
      data-testid="public-site-nav"
    >
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <button
          onClick={() => scrollTo('top')}
          className="flex items-center gap-2.5 group"
          data-testid="public-site-nav-home"
        >
          {site.workspace_logo_url ? (
            <img src={site.workspace_logo_url} alt={site.workspace_name} className="w-8 h-8 rounded-lg object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold transition-transform group-hover:scale-105" style={{ background: brand }}>
              {(site.workspace_name || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <span className={`font-semibold text-[15px] ${t.textPrimary}`}>{site.workspace_name}</span>
        </button>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-1">
          {navItems.slice(0, -1).map((it) => (
            <button
              key={it.id}
              onClick={() => scrollTo(it.id)}
              className={`px-3 py-2 rounded-md text-sm font-medium ${t.textMuted} hover:${t.textPrimary} hover:bg-black/5 dark:hover:bg-white/5 transition-colors`}
              data-testid={`public-site-nav-${it.id}`}
            >
              {it.label}
            </button>
          ))}
          <button
            onClick={() => scrollTo('contact')}
            className="ml-2 inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-semibold text-white shadow-sm hover:shadow-md transition-all"
            style={{ background: brand }}
            data-testid="public-site-nav-cta"
          >
            Contact us
          </button>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setOpen((v) => !v)}
          className={`md:hidden p-2 rounded-md ${t.textPrimary}`}
          aria-label="Toggle menu"
          data-testid="public-site-nav-toggle"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className={`md:hidden border-t ${t.navBorder} ${t.navBg} backdrop-blur-md`}>
          <div className="max-w-6xl mx-auto px-6 py-3 flex flex-col gap-1">
            {navItems.map((it) => (
              <button
                key={it.id}
                onClick={() => scrollTo(it.id)}
                className={`text-left px-3 py-2.5 rounded-md text-sm font-medium ${t.textMuted} hover:${t.textPrimary} hover:bg-black/5 dark:hover:bg-white/5 transition-colors`}
              >
                {it.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}

// ── Hero ───────────────────────────────────────────────────────────────────
function Hero({ s, site, t, brand, isDark }) {
  return (
    <section className={`relative overflow-hidden ${t.heroBg}`}>
      {/* Decorative brand glow */}
      <div
        aria-hidden
        className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full blur-3xl opacity-50 pointer-events-none"
        style={{ background: hexToRgba(brand, isDark ? 0.25 : 0.12) }}
      />
      <div
        aria-hidden
        className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full blur-3xl opacity-40 pointer-events-none"
        style={{ background: hexToRgba(brand, isDark ? 0.18 : 0.08) }}
      />

      <div className="relative max-w-6xl mx-auto px-6 pt-28 pb-24 sm:pt-36 sm:pb-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl"
        >
          {s.eyebrow && (
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-[0.12em] mb-6"
              style={{
                background: hexToRgba(brand, isDark ? 0.2 : 0.1),
                color: isDark ? '#fff' : brand,
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: brand }} />
              {s.eyebrow}
            </span>
          )}
          <h1
            className={`text-4xl sm:text-5xl lg:text-7xl ${t.headlineWeight} ${t.headlineTracking} leading-[1.05]`}
            style={{ fontFamily: t.headingFont }}
          >
            {s.headline || `Welcome to ${site.workspace_name}.`}
          </h1>
          {s.subheadline && (
            <p className={`mt-6 text-lg sm:text-xl ${t.textMuted} max-w-2xl leading-relaxed`}>
              {s.subheadline}
            </p>
          )}
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <a
              href={s.cta_link || '#contact'}
              className="inline-flex items-center gap-2 h-12 px-7 rounded-full text-base font-semibold text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
              style={{ background: brand }}
              data-testid="site-hero-cta"
            >
              {s.cta_label || 'Get in touch'}
              <ArrowRight className="w-4 h-4" />
            </a>
            {s.secondary_cta_label && (
              <a
                href={s.secondary_cta_link || '#about'}
                className={`inline-flex items-center gap-2 h-12 px-6 rounded-full text-base font-semibold border-2 transition-colors ${
                  isDark
                    ? 'border-zinc-700 text-white hover:bg-white/5'
                    : 'border-zinc-300 text-zinc-900 hover:bg-zinc-100'
                }`}
                data-testid="site-hero-secondary-cta"
              >
                {s.secondary_cta_label}
              </a>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ── Trust bar ──────────────────────────────────────────────────────────────
function TrustBar({ s, t, brand, isDark }) {
  return (
    <section className={`${t.pageBg} border-y ${isDark ? 'border-zinc-800' : 'border-zinc-200'} py-10`}>
      <motion.div {...REVEAL} className="max-w-6xl mx-auto px-6">
        {s.title && (
          <p className={`text-xs font-bold uppercase tracking-[0.18em] text-center mb-6 ${t.textSubtle}`}>
            {s.title}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6 opacity-80">
          {s.items.map((it, i) => (
            <div key={i} className="flex items-center gap-2">
              {it.image_url ? (
                <img src={it.image_url} alt={it.label} className="h-7 sm:h-8 max-w-[140px] object-contain opacity-90" />
              ) : (
                <span className={`text-sm font-semibold ${t.textMuted}`}>{it.label}</span>
              )}
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}

// ── Stats ──────────────────────────────────────────────────────────────────
function Stats({ s, t, brand, isDark }) {
  const cols = s.items.length === 4 ? 'sm:grid-cols-4' : s.items.length === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-3';
  return (
    <section className={`${t.altBg} py-16 sm:py-20`}>
      <motion.div {...REVEAL} className="max-w-5xl mx-auto px-6">
        {s.title && (
          <h2 className={`text-2xl sm:text-3xl ${t.headlineWeight} ${t.headlineTracking} text-center mb-10`} style={{ fontFamily: t.headingFont }}>
            {s.title}
          </h2>
        )}
        <div className={`grid grid-cols-2 ${cols} gap-6`}>
          {s.items.map((it, i) => (
            <div key={i} className="text-center" data-testid={`site-stat-${i}`}>
              <p className={`text-3xl sm:text-5xl ${t.headlineWeight} ${t.headlineTracking}`} style={{ color: brand, fontFamily: t.headingFont }}>
                {it.value}
              </p>
              <p className={`mt-2 text-sm font-medium ${t.textMuted}`}>{it.label}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}

// ── Services ───────────────────────────────────────────────────────────────
function Services({ s, t, brand, isDark }) {
  return (
    <section id="services" className={`${t.pageBg} py-20 sm:py-28`}>
      <motion.div {...REVEAL} className="max-w-6xl mx-auto px-6">
        <SectionLabel brand={brand}>What we do</SectionLabel>
        <div className="mt-3 mb-12 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <h2 className={`text-3xl sm:text-4xl ${t.headlineWeight} ${t.headlineTracking} max-w-2xl`} style={{ fontFamily: t.headingFont }}>
            {s.title || 'Services'}
          </h2>
          {s.subtitle && (
            <p className={`text-base ${t.textMuted} max-w-md`}>{s.subtitle}</p>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {s.items.map((svc, i) => {
            const Icon = SERVICE_ICONS[svc.icon] || Briefcase;
            return (
              <motion.div
                key={svc.id || i}
                {...REVEAL}
                transition={{ ...REVEAL.transition, delay: 0.05 * i }}
                className={`group rounded-2xl border ${t.cardBorder} ${t.cardBg} p-6 flex flex-col hover:-translate-y-1 hover:shadow-xl transition-all duration-300`}
                data-testid={`site-service-${i}`}
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                  style={{ background: hexToRgba(brand, isDark ? 0.22 : 0.1) }}
                >
                  <Icon className="w-5 h-5" style={{ color: brand }} />
                </div>
                <h3 className={`text-lg font-semibold mb-2 ${t.textPrimary}`} style={{ fontFamily: t.headingFont }}>
                  {svc.title}
                </h3>
                {svc.description && (
                  <p className={`text-sm leading-relaxed mb-5 flex-1 ${t.textMuted}`}>
                    {svc.description}
                  </p>
                )}
                <div className="flex items-center justify-between mt-auto pt-2 gap-2">
                  {svc.price ? (
                    <span
                      className="inline-block px-2.5 py-1 text-xs font-bold rounded-md"
                      style={{ background: hexToRgba(brand, isDark ? 0.22 : 0.12), color: isDark ? '#fff' : brand }}
                    >
                      {svc.price}
                    </span>
                  ) : <span />}
                  {svc.cta_link && (
                    <a
                      href={svc.cta_link}
                      target={svc.cta_link.startsWith('#') ? undefined : '_blank'}
                      rel="noopener noreferrer"
                      className="text-sm font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all"
                      style={{ color: brand }}
                    >
                      {svc.cta_label || 'Learn more'}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </section>
  );
}

// ── Testimonials ───────────────────────────────────────────────────────────
function Testimonials({ s, t, brand, isDark }) {
  return (
    <section id="testimonials" className={`${t.altBg} py-20 sm:py-28`}>
      <motion.div {...REVEAL} className="max-w-6xl mx-auto px-6">
        <SectionLabel brand={brand}>Loved by clients</SectionLabel>
        <h2 className={`text-3xl sm:text-4xl ${t.headlineWeight} ${t.headlineTracking} mt-3 mb-12 max-w-2xl`} style={{ fontFamily: t.headingFont }}>
          Don't just take our word for it.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {s.items.map((q, i) => {
            const rating = Math.max(1, Math.min(5, Number(q.rating) || 5));
            return (
              <motion.div
                key={q.id || i}
                {...REVEAL}
                transition={{ ...REVEAL.transition, delay: 0.05 * i }}
                className={`rounded-2xl border ${t.cardBorder} ${t.cardBg} p-6 flex flex-col`}
                data-testid={`site-testimonial-${i}`}
              >
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: 5 }).map((_, k) => (
                    <Star
                      key={k}
                      className="w-4 h-4"
                      style={{ color: k < rating ? brand : (isDark ? '#3f3f46' : '#e4e4e7') }}
                      fill={k < rating ? brand : 'transparent'}
                    />
                  ))}
                </div>
                <p className={`text-base leading-relaxed mb-6 flex-1 ${t.textPrimary}`}>
                  "{q.quote}"
                </p>
                <div className="flex items-center gap-3">
                  {q.avatar_url ? (
                    <img src={q.avatar_url} alt={q.author} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white"
                      style={{ background: brand }}
                    >
                      {(q.author || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p className={`text-sm font-semibold ${t.textPrimary}`}>{q.author}</p>
                    {q.role && <p className={`text-xs ${t.textSubtle}`}>{q.role}</p>}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </section>
  );
}

// ── FAQ ────────────────────────────────────────────────────────────────────
function FAQ({ s, t, brand, isDark }) {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className={`${t.pageBg} py-20 sm:py-28`}>
      <motion.div {...REVEAL} className="max-w-3xl mx-auto px-6">
        <SectionLabel brand={brand}>FAQ</SectionLabel>
        <h2 className={`text-3xl sm:text-4xl ${t.headlineWeight} ${t.headlineTracking} mt-3 mb-10`} style={{ fontFamily: t.headingFont }}>
          {s.title || 'Frequently asked questions'}
        </h2>
        <div className={`divide-y ${isDark ? 'divide-zinc-800' : 'divide-zinc-200'}`}>
          {s.items.map((it, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="py-1">
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 py-5 text-left group"
                  data-testid={`site-faq-${i}`}
                >
                  <span className={`text-base sm:text-lg font-semibold ${t.textPrimary} group-hover:opacity-90`}>
                    {it.question}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    style={{ color: brand }}
                  />
                </button>
                {isOpen && (
                  <div className="pb-6 pr-9">
                    <p className={`text-base leading-relaxed ${t.textMuted} whitespace-pre-line`}>
                      {it.answer}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </motion.div>
    </section>
  );
}

// ── Hours / contact info ───────────────────────────────────────────────────
function HoursContact({ s, t, brand, isDark }) {
  const blocks = [
    s.hours && { Icon: Clock, label: 'Hours', value: s.hours },
    s.address && { Icon: MapPin, label: 'Visit us', value: s.address },
    s.phone && { Icon: Phone, label: 'Call', value: s.phone, href: `tel:${s.phone}` },
    s.email && { Icon: Mail, label: 'Email', value: s.email, href: `mailto:${s.email}` },
  ].filter(Boolean);
  if (blocks.length === 0) return null;

  return (
    <section className={`${t.altBg} py-16`}>
      <motion.div {...REVEAL} className="max-w-5xl mx-auto px-6">
        <div className={`grid gap-6 ${blocks.length >= 4 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2'}`}>
          {blocks.map(({ Icon, label, value, href }, i) => (
            <div key={i} className={`rounded-2xl border ${t.cardBorder} ${t.cardBg} p-5 flex items-start gap-3`}>
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: hexToRgba(brand, isDark ? 0.22 : 0.1) }}
              >
                <Icon className="w-5 h-5" style={{ color: brand }} />
              </div>
              <div className="min-w-0">
                <p className={`text-sm font-semibold ${t.textPrimary}`}>{label}</p>
                {href ? (
                  <a href={href} className={`text-sm hover:underline whitespace-pre-line break-words ${t.textMuted}`}>{value}</a>
                ) : (
                  <p className={`text-sm whitespace-pre-line ${t.textMuted}`}>{value}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}

// ── Footer ─────────────────────────────────────────────────────────────────
function Footer({ site, s, brand, isDark, t }) {
  const onCustomDomain = !!site.custom_domain;
  const showPoweredBy = (s.footer?.show_powered_by !== false) && !onCustomDomain;
  return (
    <footer className={`${isDark ? 'bg-zinc-950 text-zinc-400 border-t border-zinc-800' : 'bg-zinc-900 text-zinc-300'} py-12`}>
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {site.workspace_logo_url ? (
              <img src={site.workspace_logo_url} alt={site.workspace_name} className="w-9 h-9 rounded-lg object-cover" />
            ) : (
              <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-sm font-bold" style={{ background: brand }}>
                {(site.workspace_name || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <p className="font-semibold text-white">{site.workspace_name}</p>
              {s.footer?.extra_text && (
                <p className="text-sm mt-0.5 text-zinc-400">{s.footer.extra_text}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {Object.entries(s.footer?.social || {}).filter(([, v]) => v).map(([k, v]) => {
              const Icon = SOCIAL_ICONS[k] || GlobeIcon;
              return (
                <a
                  key={k}
                  href={v}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg flex items-center justify-center bg-white/5 text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <Icon className="w-4 h-4" />
                </a>
              );
            })}
          </div>
        </div>
        <div className="mt-8 pt-6 border-t border-zinc-800 flex flex-col sm:flex-row justify-between text-xs text-zinc-500 gap-2">
          <span>© {new Date().getFullYear()} {site.workspace_name}. All rights reserved.</span>
          {showPoweredBy && (
            <span>Powered by <a href="https://countbucks.com" target="_blank" rel="noopener noreferrer" className="hover:text-white">CountBucks</a></span>
          )}
        </div>
      </div>
    </footer>
  );
}

// ── Contact form ───────────────────────────────────────────────────────────
function ContactFormSection({ slug, theme, brand, isDark, title, intro, services }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', service: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Please enter your name and email');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/public/sites/${slug}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const j = await r.json();
      if (!r.ok) {
        toast.error(j.detail || 'Submission failed');
        return;
      }
      setSubmitted(true);
    } catch {
      toast.error('Network error — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = `w-full h-11 px-3.5 rounded-lg border focus:outline-none focus:ring-2 transition-all ${
    isDark
      ? 'bg-zinc-950 border-zinc-700 text-white placeholder:text-zinc-500'
      : 'bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400'
  }`;

  return (
    <section
      id="contact"
      className={`${theme.altBg} py-20 sm:py-28`}
      data-testid="site-contact-section"
    >
      <motion.div {...REVEAL} className="max-w-2xl mx-auto px-6">
        <SectionLabel brand={brand}>Get in touch</SectionLabel>
        <h2 className={`text-3xl sm:text-4xl ${theme.headlineWeight} ${theme.headlineTracking} mt-3 mb-3`} style={{ fontFamily: theme.headingFont }}>
          {title || "Let's work together."}
        </h2>
        {intro && (
          <p className={`text-base mb-10 leading-relaxed ${theme.textMuted}`}>
            {intro}
          </p>
        )}

        {submitted ? (
          <div className={`rounded-2xl border ${theme.cardBorder} ${theme.cardBg} p-8 text-center`} data-testid="site-contact-success">
            <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4" style={{ background: hexToRgba(brand, 0.15) }}>
              <CheckCircle2 className="w-8 h-8" style={{ color: brand }} />
            </div>
            <h3 className={`text-xl font-semibold mb-2 ${theme.textPrimary}`}>Thank you!</h3>
            <p className={theme.textMuted}>
              We've got your message and will be in touch within 24 hours.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={`rounded-2xl border ${theme.cardBorder} ${theme.cardBg} p-6 sm:p-8 space-y-4`} data-testid="site-contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name *"
                required
                className={inputCls}
                style={{ '--tw-ring-color': brand }}
                data-testid="site-contact-name"
              />
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email *"
                required
                className={inputCls}
                data-testid="site-contact-email"
              />
            </div>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="Phone (optional)"
              className={inputCls}
              data-testid="site-contact-phone"
            />
            {services.length > 0 && (
              <select
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
                className={inputCls}
                data-testid="site-contact-service"
              >
                <option value="">Which service are you interested in?</option>
                {services.map((svc, i) => (
                  <option key={svc.id || i} value={svc.title}>{svc.title}</option>
                ))}
              </select>
            )}
            <textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={4}
              placeholder="Tell us about your project (optional)"
              className={`w-full p-3.5 rounded-lg border focus:outline-none focus:ring-2 transition-all ${
                isDark
                  ? 'bg-zinc-950 border-zinc-700 text-white placeholder:text-zinc-500'
                  : 'bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400'
              }`}
              data-testid="site-contact-message"
            />
            <button
              type="submit"
              disabled={submitting}
              className="w-full h-12 rounded-lg text-base font-semibold text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:transform-none"
              style={{ background: brand }}
              data-testid="site-contact-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Send message<Send className="w-4 h-4" /></>)}
            </button>
          </form>
        )}
      </motion.div>
    </section>
  );
}
